import { useState } from 'react';
import { Alert, Button, Form, InputGroup, Modal, Stack } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { faEye, faEyeSlash } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import useLocalStore from 'src/store/useLocalStore';
import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import { Api } from 'src/models/Api.model';
import { I18NextNS } from 'src/models/Language.model';

import useAutoConnectCamera from 'src/hooks/useAutoConnectCamera';
import useHandleHttpRequestError from 'src/hooks/useHandleHttpRequestError';
import useValidatePassword from 'src/hooks/useValidatePassword';

export interface ResetPasswordFormData {
  otp: string;
  password: string;
  confirmPassword: string;
}

interface ResetPasswordFormProps {
  userId: string;
  currentPassword: string;
}

const ResetPasswordForm = ({
  userId,
  currentPassword,
}: ResetPasswordFormProps) => {
  const { t } = useTranslation(I18NextNS.PAGES);
  const history = useHistory();
  const { showHttpRequestError } = useHandleHttpRequestError();

  const [isShowPassword, setIsShowPassword] = useState(false);
  const [isShowConfirmPassword, setIsShowConfirmPassword] = useState(false);

  const { currentUser } = useRemoteBoundStore();

  const { setRememberUserEmail, isRememberEmail } = useLocalStore();

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
    watch,
  } = useForm<ResetPasswordFormData>({
    defaultValues: {
      otp: '',
      password: '',
      confirmPassword: '',
    },
    criteriaMode: 'all',
  });

  const password = watch('password');

  const { autoConnectCamera } = useAutoConnectCamera();

  const handleResetPassword = async (data: ResetPasswordFormData) => {
    try {
      await Api.remote.change_password(
        userId,
        currentPassword,
        data.password.trim(),
      );

      autoConnectCamera();
      if (isRememberEmail) {
        setRememberUserEmail(currentUser?.emailAddress);
      } else {
        setRememberUserEmail(undefined);
      }

      history.replace('/');
    } catch (error) {
      showHttpRequestError(error);
    }
  };

  return (
    <Form onSubmit={handleSubmit(handleResetPassword)}>
      <Modal.Body>
        <Stack className="text-center" gap={2}>
          <h2 data-test="login-change-password-title">
            {t('changePassword.title')}
          </h2>
          <Alert
            variant="warning"
            data-test="login-change-password-expired-alert"
          >
            {t('loginLogout.passwordExpired')}
          </Alert>
        </Stack>

        <Form.Group className="mb-2">
          <Form.Label>
            <strong>{t('changePassword.newPassword')}</strong>
          </Form.Label>
          <InputGroup>
            <Form.Control
              type={isShowPassword ? 'text' : 'password'}
              placeholder={t('changePassword.newPasswordPlaceholder')}
              autoComplete="off"
              data-test="login-change-password-new-input"
              {...register('password', {
                required: t('changePassword.passwordRequired'),
                validate: useValidatePassword(),
              })}
            />
            <InputGroup.Text
              onClick={() => setIsShowPassword(!isShowPassword)}
              className="pointer"
              data-test="login-change-password-new-visibility-toggle"
            >
              <FontAwesomeIcon icon={isShowPassword ? faEye : faEyeSlash} />
            </InputGroup.Text>
          </InputGroup>
          <div className="text-danger">
            {errors.password?.type === 'required' ? (
              <p>{t('changePassword.newPasswordRequired')}</p>
            ) : (
              <ul className="list-unstyled">
                {errors.password?.types?.minLength && (
                  <li>{errors.password.message}</li>
                )}
                {errors.password?.types?.hasUpperCase && (
                  <li>{errors.password.types.hasUpperCase}</li>
                )}
                {errors.password?.types?.hasLowerCase && (
                  <li>{errors.password.types.hasLowerCase}</li>
                )}
                {errors.password?.types?.hasNumber && (
                  <li>{errors.password.types.hasNumber}</li>
                )}
                {errors.password?.types?.hasSpecialChar && (
                  <li>{errors.password.types.hasSpecialChar}</li>
                )}
              </ul>
            )}
          </div>
        </Form.Group>

        <Form.Group className="mb-2">
          <Form.Label>
            <strong>{t('changePassword.confirmPassword')}</strong>
          </Form.Label>
          <InputGroup>
            <Form.Control
              type={isShowConfirmPassword ? 'text' : 'password'}
              placeholder={t('changePassword.confirmPasswordPlaceholder')}
              autoComplete="off"
              data-test="login-change-password-confirm-input"
              {...register('confirmPassword', {
                required: t('changePassword.confirmPasswordRequired'),
                validate: (value) =>
                  value === password || t('changePassword.passwordsDoNotMatch'),
              })}
            />
            <InputGroup.Text
              onClick={() => setIsShowConfirmPassword(!isShowConfirmPassword)}
              className="pointer"
              data-test="login-change-password-confirm-visibility-toggle"
            >
              <FontAwesomeIcon
                icon={isShowConfirmPassword ? faEye : faEyeSlash}
              />
            </InputGroup.Text>
          </InputGroup>
          {<p className="text-danger ">{errors.confirmPassword?.message}</p>}
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="submit"
          disabled={isSubmitting}
          data-test="login-change-password-submit-button"
          data-track="auth.reset-password.submit"
          data-observe
        >
          {t('changePassword.change')}
        </Button>
      </Modal.Footer>
    </Form>
  );
};

export default ResetPasswordForm;
